import { Button, Card, CardBody, Flex, Skeleton, Stack, Text } from "@chakra-ui/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useGetPortalSourcingEventQuery } from "../../../../autogen/bff-api";
import { isFetchBaseQueryError } from "../../../../common/errors/isFetchBaseQueryError";
import { useApiError } from "../../../../common/errors/useApiError";
import { urls } from "../../../../urls";
import { PortalLayout } from "../components/PortalLayout";
import { useCurrentMoment } from "../utils/useCurrentMoment";
import { BackToListingLink } from "./BackToListingLink";
import { EventAboutSection } from "./EventAboutSection";
import { EventAwardCriteriaSection } from "./EventAwardCriteriaSection";
import { EventAwardNotificationSection } from "./EventAwardNotificationSection";
import { EventBuyerInfo } from "./EventBuyerInfo";
import { EventContractDetails } from "./EventContractDetails";
import { EventDeadlineHighlight } from "./EventDeadlineHighlight";
import { EventCommunicationSection } from "./communication/EventCommunicationSection";
import { EventDocumentsSection } from "./EventDocumentsSection";
import { EventHero } from "./EventHero";
import { EventLifecycleBanner } from "./EventLifecycleBanner";
import { EventProcessTimeline } from "./EventProcessTimeline";
import { EventQualificationSection } from "./EventQualificationSection";
import { RegisterInterestCta } from "./RegisterInterestCta";
import { StartBidCta } from "../bid/StartBidCta";

export const PortalSourcingEventPage = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const displayer = useApiError();
  const now = useCurrentMoment();

  const { data, error, isLoading } = useGetPortalSourcingEventQuery(
    { eventId: eventId ?? "" },
    { skip: !eventId, refetchOnFocus: true }
  );

  const isNotFound = error != null && isFetchBaseQueryError(error) && error.status === 404;

  useEffect(() => {
    if (error && !isNotFound && data) {
      displayer.triggerSoft(error);
    }
  }, [displayer, error, isNotFound, data]);

  if (isNotFound) {
    return (
      <PortalLayout>
        <Stack spacing="6">
          <BackToListingLink />
          <NotFoundCard />
        </Stack>
      </PortalLayout>
    );
  }

  if (error && !data) return <PortalLayout>{displayer.show(error)}</PortalLayout>;

  return (
    <PortalLayout>
      <Stack spacing="6">
        <BackToListingLink />

        {isLoading || !data ? (
          <LoadingSkeleton />
        ) : (
          <>
            <EventHero event={data} />
            <EventLifecycleBanner event={data} />
            <EventAwardNotificationSection event={data} />
            <EventDeadlineHighlight event={data} />
            <RegisterInterestCta event={data} />
            <StartBidCta event={data} />
            <EventProcessTimeline event={data} now={now} />
            <EventAboutSection event={data} />
            <EventAwardCriteriaSection event={data} />
            <EventQualificationSection event={data} />
            <EventDocumentsSection event={data} />
            <EventCommunicationSection event={data} />
            <EventContractDetails event={data} />
            <EventBuyerInfo event={data} />
          </>
        )}
      </Stack>
    </PortalLayout>
  );
};

const LoadingSkeleton = () => (
  <Stack spacing="6">
    <Skeleton height="80px" rounded="md" startColor="smBackground" endColor="smBackgroundTertiary" />
    <Skeleton height="100px" rounded="xl" startColor="smBackground" endColor="smBackgroundTertiary" />
    <Skeleton height="200px" rounded="xl" startColor="smBackground" endColor="smBackgroundTertiary" />
    <Skeleton height="180px" rounded="xl" startColor="smBackground" endColor="smBackgroundTertiary" />
    <Skeleton height="160px" rounded="xl" startColor="smBackground" endColor="smBackgroundTertiary" />
  </Stack>
);

const NotFoundCard = () => {
  const { t } = useTranslation();
  return (
    <Card variant="outline" rounded="xl">
      <CardBody py="20">
        <Flex direction="column" align="center" justify="center" gap="4">
          <Text color="smMuted" textAlign="center">
            {t("Procurement not found or no longer available")}
          </Text>
          <Button as={RouterLink} to={urls.portal.list} variant="outline" colorScheme="gray">
            {t("Back to listing")}
          </Button>
        </Flex>
      </CardBody>
    </Card>
  );
};
